import React from "react";

import SvgChevronDown from "@uui/ChevronDown";
import SvgChevronLeft from "@uui/ChevronLeft";
import SvgChevronRight from "@uui/ChevronRight";
import { DateTime } from "luxon";

import { styled } from "@mui/material";
import {
  BaseSingleInputFieldProps,
  DateField,
  DateFieldProps,
  DatePickerProps,
  DateValidationError,
  FieldSection,
  DatePicker as MuiDatePicker,
  UseDateFieldProps,
} from "@mui/x-date-pickers";

import useAnchorElement from "@app/utils/hooks/useAnchorElement";

const popperStyles = {
  sx: {
    ".MuiPickersArrowSwitcher-button": {
      borderRadius: "2px",
      fontSize: "20px",
    },
    ".MuiPickersCalendarHeader-switchViewIcon": {
      fontSize: "20px",
    },
    ".MuiPickersCalendarHeader-switchViewButton": {
      borderRadius: "2px",
    },
    ".MuiPickersDay-root": {
      borderRadius: "2px",
    },
    ".MuiPickersDay-today": {
      borderRadius: "2px",
    },
    ".MuiPickersDay-root.Mui-selected": {
      borderRadius: "2px",
    },
  },
};

const DateFieldWithRef = React.forwardRef<HTMLDivElement>((props, ref) => (
  <DateField ref={ref} {...props} />
));
DateFieldWithRef.displayName = "DateField";

const StyledDateField = styled((props: DateFieldProps<DateTime>) => (
  <DateFieldWithRef {...props} />
))(({ theme }) => ({
  ".MuiInputBase-input": {
    fontSize: "14px",
    height: "32px",
  },
  ".MuiInputBase-formControl": {
    maxWidth: "140px",
    fontWeight: 400,
    height: "32px",
  },
  ".MuiOutlinedInput-notchedOutline": {
    height: "37px",
    borderColor: theme.palette.shades.neutral[100],
    borderRadius: "4px",
    color: theme.palette.shades.neutral[800],
  },
}));

interface CustomDateFieldProps
  extends UseDateFieldProps<DateTime, true>,
    BaseSingleInputFieldProps<
      DateTime | null,
      DateTime,
      FieldSection,
      true,
      DateValidationError
    > {
  onClick?: () => void;
}

const CustomDateField = React.forwardRef<HTMLDivElement>(
  (props: CustomDateFieldProps, ref) => {
    const {
      onClick,
      id,
      disabled,
      value,
      onChange,
      inputProps: { "aria-label": ariaLabel } = {},
    } = props;
    return (
      <StyledDateField
        variant="outlined"
        id={id}
        disabled={disabled}
        ref={ref}
        aria-label={ariaLabel}
        onClick={onClick}
        value={value}
        onChange={(newValue, ctx) => onChange?.(newValue, ctx)}
        formatDensity="spacious"
      />
    );
  }
);

CustomDateField.displayName = "CustomDateField";

const CustomDatePicker = (
  props: Omit<DatePickerProps<DateTime>, "open" | "onOpen" | "onClose">
) => {
  const [open, setOpen] = React.useState(false);
  const { anchorElement: anchorEl, onClick: onClickAnchorElement } =
    useAnchorElement();

  const handleClick = (e: any) => {
    onClickAnchorElement(e);
    setOpen((open) => !open);
  };

  return (
    <MuiDatePicker
      slots={{
        field: CustomDateField,
        rightArrowIcon: SvgChevronRight,
        leftArrowIcon: SvgChevronLeft,
        switchViewIcon: SvgChevronDown,
        ...props.slots,
      }}
      slotProps={{
        field: { onClick: handleClick } as any,
        popper: { anchorEl, ...popperStyles },
      }}
      {...props}
      open={open}
      onClose={() => setOpen(false)}
      onOpen={() => setOpen(true)}
    />
  );
};

const DatePicker = (props: DatePickerProps<DateTime>) => {
  return <CustomDatePicker {...props} />;
};

export default DatePicker;
